/**
 * ChatHeader Component
 * ====================
 * 
 * Header section of chat: back button, user info, video button, menu.
 * 
 * Props-driven, no internal state except menu open/close.
 * All handlers passed from parent.
 */
import React from 'react';
import { ArrowLeft, Video } from 'lucide-react';
import { OnlineDot } from '../OnlineDot';
import { getEliteUpgradeUrl } from '../../utils/entitlements';

export function ChatHeader({
  match,
  otherOnline,
  isVideoAvailable,
  canUseVideoChat,
  videoUnlocked,
  videoInviteSent,
  isStartingVideo,
  onVideoInvite,
  onBack,
  onInboxClick,
  onMenuToggle,
  showMoreMenu,
  menuContent,
}) {
  return (
    <header 
      className="bg-white/90 backdrop-blur-sm border-b border-rose-100 sticky top-0 z-40"
      data-testid="chat-header"
    >
      <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between py-3 sm:py-4">
          {/* Back Button */}
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-700 hover:text-rose-600"
            data-testid="chat-back-btn"
            type="button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:block">Back</span>
          </button>
          
          {/* Explicit inbox escape hatch */}
          <button
            type="button"
            onClick={onInboxClick}
            className="ml-2 text-sm px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-gray-600 hover:text-rose-600 border border-gray-200"
            data-testid="back-to-inbox-btn"
          >
            Inbox
          </button>
          
          <div className="flex items-center justify-between flex-1">
            {/* User Info */}
            <div className="flex items-center space-x-3 ml-2">
              <div className="w-10 h-10 bg-gradient-rose-gold rounded-full flex items-center justify-center">
                <span className="font-semibold text-gray-700">
                  {match?.profile?.names?.[0]?.charAt(0) || '💬'}
                </span>
              </div>
              <div>
                <div className="font-playfair font-semibold text-gray-900">
                  {match?.profile?.names?.join(' & ') || 'Chat'}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {match?.profile?.location || (match ? '' : 'Loading...')}
                  </span>
                  {otherOnline && <OnlineDot />}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Video Chat Button */}
              {isVideoAvailable && (
                <VideoButton
                  canUseVideoChat={canUseVideoChat}
                  videoUnlocked={videoUnlocked}
                  videoInviteSent={videoInviteSent}
                  isStartingVideo={isStartingVideo}
                  onVideoInvite={onVideoInvite}
                />
              )}

              {/* Menu Button */}
              <div className="relative">
                <button
                  onClick={onMenuToggle}
                  className="ml-2 p-2 rounded-full hover:bg-gray-100 text-gray-600"
                  title="More options"
                  type="button"
                  data-testid="chat-menu-btn"
                >
                  •••
                </button>
                {showMoreMenu && menuContent}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * VideoButton - Renders appropriate video button state
 */
function VideoButton({
  canUseVideoChat,
  videoUnlocked,
  videoInviteSent,
  isStartingVideo,
  onVideoInvite,
}) {
  // Non-Elite: Show locked button
  if (!canUseVideoChat) {
    return (
      <a
        href={getEliteUpgradeUrl('video_call')}
        className="p-2 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-600 hover:from-amber-200 hover:to-yellow-200"
        title="Elite members can start video calls"
        aria-label="Start video call (Elite feature)"
        data-testid="video-chat-btn-elite-locked"
      >
        <span className="flex items-center gap-1">
          <Video className="w-5 h-5" />
          <span className="text-xs">👑</span>
        </span>
      </a>
    );
  }
  
  // Elite + unlocked: Can start video
  if (videoUnlocked) {
    return (
      <button
        onClick={onVideoInvite}
        disabled={videoInviteSent || isStartingVideo}
        className={`p-2 rounded-full ${
          videoInviteSent || isStartingVideo
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
            : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
        }`}
        title={
          isStartingVideo 
            ? 'Checking...' 
            : videoInviteSent 
            ? 'Video invite sent' 
            : 'Start video chat'
        }
        data-testid="video-chat-btn"
        type="button"
      >
        <Video className="w-5 h-5" />
      </button>
    );
  }
  
  // Elite but need more messages first
  return (
    <button
      disabled
      className="p-2 rounded-full bg-gray-200 text-gray-400 cursor-not-allowed opacity-70"
      title="🔒 Send a few messages each to unlock video"
      data-testid="video-chat-btn-locked"
      type="button"
    >
      <Video className="w-5 h-5" />
    </button>
  );
}

export default ChatHeader;
